/**
 * ExperimentHistoryModal Component
 * 
 * Modal for viewing and loading past experiment transactions
 * Shows numbered transactions grouped by experiment ID
 */

import React, { useState, useEffect } from 'react';
import { X, Trash2, Download, Calendar, Clock, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import { ChemExperimentState } from './types';
import { 
  getHistory, 
  loadFromHistory, 
  deleteHistoryEntry, 
  saveToHistory, 
  clearHistory,
  getTransactionsByExperiment,
  getTransactions,
  loadFromTransaction,
  deleteTransaction,
  ExperimentTransaction,
} from './experimentStorage';

interface ExperimentHistoryModalProps {
  currentExperiment: ChemExperimentState;
  onClose: () => void;
  onLoad: (experiment: ChemExperimentState) => void;
}

export const ExperimentHistoryModal: React.FC<ExperimentHistoryModalProps> = ({
  currentExperiment,
  onClose,
  onLoad,
}) => {
  const [history, setHistory] = useState(getHistory());
  const [transactionsByExperiment, setTransactionsByExperiment] = useState<Record<string, ExperimentTransaction[]>>({});
  const [expandedExperiments, setExpandedExperiments] = useState<Set<string>>(new Set());
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'transactions' | 'snapshots'>('transactions');

  useEffect(() => {
    refreshData();
  }, []);

  const refreshData = () => {
    setHistory(getHistory());
    setTransactionsByExperiment(getTransactionsByExperiment());
  };

  const toggleExperiment = (experimentId: string) => {
    const newExpanded = new Set(expandedExperiments);
    if (newExpanded.has(experimentId)) {
      newExpanded.delete(experimentId);
    } else {
      newExpanded.add(experimentId);
    }
    setExpandedExperiments(newExpanded);
  };

  const handleSaveCurrent = () => {
    const name = prompt('Enter a name for this experiment:', currentExperiment.reactionName || '');
    if (name) {
      saveToHistory(currentExperiment, name);
      refreshData();
    }
  };

  const handleLoadTransaction = (transactionId: string) => {
    const experiment = loadFromTransaction(transactionId);
    if (experiment) {
      onLoad(experiment);
      onClose();
    }
  };

  const handleLoadSnapshot = (id: string) => {
    const experiment = loadFromHistory(id);
    if (experiment) {
      onLoad(experiment);
      onClose();
    }
  };

  const handleDeleteTransaction = (transactionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      deleteTransaction(transactionId);
      refreshData();
    }
  };

  const handleDeleteSnapshot = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this history entry?')) {
      deleteHistoryEntry(id);
      refreshData();
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all history? This cannot be undone.')) {
      clearHistory();
      refreshData();
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      reaction_setup: 'Reaction Setup',
      material_added: 'Material Added',
      material_removed: 'Material Removed',
      calculation_run: 'Calculation Run',
      step_added: 'Step Added',
      step_removed: 'Step Removed',
      manual_save: 'Manual Save',
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      reaction_setup: '#2563eb',
      material_added: '#10b981',
      material_removed: '#ef4444',
      calculation_run: '#f59e0b',
      step_added: '#8b5cf6',
      step_removed: '#ef4444',
      manual_save: '#64748b',
    };
    return colors[type] || '#64748b';
  };

  const allTransactionExperiments = Object.keys(transactionsByExperiment);
  const hasTransactions = allTransactionExperiments.length > 0;
  const hasSnapshots = history.length > 0;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Experiment History</h2>
          <div style={styles.headerActions}>
            <div style={styles.viewModeToggle}>
              <button
                style={{
                  ...styles.viewModeButton,
                  ...(viewMode === 'transactions' ? styles.viewModeButtonActive : {}),
                }}
                onClick={() => setViewMode('transactions')}
              >
                Transactions
              </button>
              <button
                style={{
                  ...styles.viewModeButton,
                  ...(viewMode === 'snapshots' ? styles.viewModeButtonActive : {}),
                }}
                onClick={() => setViewMode('snapshots')}
              >
                Snapshots
              </button>
            </div>
            <button style={styles.saveButton} onClick={handleSaveCurrent}>
              <Download size={16} />
              Save Current
            </button>
            {(hasTransactions || hasSnapshots) && (
              <button style={styles.clearButton} onClick={handleClearAll}>
                Clear All
              </button>
            )}
            <button style={styles.closeButton} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div style={styles.content}>
          {viewMode === 'transactions' ? (
            // Transactions View
            !hasTransactions ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>No transactions found</p>
                <p style={styles.emptySubtext}>
                  Transactions are automatically saved when you perform actions like setting up reactions or running calculations
                </p>
              </div>
            ) : (
              <div style={styles.transactionsList}>
                {allTransactionExperiments.map((experimentId) => {
                  const transactions = transactionsByExperiment[experimentId];
                  const isExpanded = expandedExperiments.has(experimentId);
                  const firstTransaction = transactions[0];
                  const experimentName = firstTransaction?.experimentSnapshot?.reactionName || 
                                         firstTransaction?.experimentSnapshot?.experimentId || 
                                         experimentId;

                  return (
                    <div key={experimentId} style={styles.experimentGroup}>
                      <div
                        style={styles.experimentHeader}
                        onClick={() => toggleExperiment(experimentId)}
                      >
                        <div style={styles.experimentHeaderLeft}>
                          {isExpanded ? (
                            <ChevronDown size={18} style={styles.chevron} />
                          ) : (
                            <ChevronRight size={18} style={styles.chevron} />
                          )}
                          <div>
                            <h3 style={styles.experimentName}>{experimentName}</h3>
                            <div style={styles.experimentMeta}>
                              <span style={styles.metaItem}>
                                {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
                              </span>
                              <span style={styles.metaItem}>
                                <Calendar size={12} />
                                {formatDate(firstTransaction.timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={styles.transactionsGroup}>
                          {transactions.map((txn) => (
                            <div
                              key={txn.id}
                              style={{
                                ...styles.transactionItem,
                                ...(selectedTransactionId === txn.id ? styles.transactionItemSelected : {}),
                              }}
                              onClick={() => setSelectedTransactionId(txn.id)}
                            >
                              <div style={styles.transactionContent}>
                                <div style={styles.transactionHeader}>
                                  <div style={styles.transactionNumber}>
                                    #{txn.transactionNumber}
                                  </div>
                                  <div
                                    style={{
                                      ...styles.transactionTypeBadge,
                                      backgroundColor: getTransactionTypeColor(txn.type) + '20',
                                      color: getTransactionTypeColor(txn.type),
                                    }}
                                  >
                                    {getTransactionTypeLabel(txn.type)}
                                  </div>
                                  <div style={styles.transactionTime}>
                                    <Clock size={12} />
                                    {formatTime(txn.timestamp)}
                                  </div>
                                </div>
                                <div style={styles.transactionDescription}>
                                  {txn.description}
                                </div>
                                {txn.details && Object.keys(txn.details).length > 0 && (
                                  <div style={styles.transactionDetails}>
                                    {Object.entries(txn.details).map(([key, value]) => (
                                      <span key={key} style={styles.detailTag}>
                                        {key}: {String(value)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div style={styles.transactionActions}>
                                <button
                                  style={styles.viewButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLoadTransaction(txn.id);
                                  }}
                                  title="Load this transaction state"
                                >
                                  <Eye size={14} />
                                  View
                                </button>
                                <button
                                  style={styles.deleteButton}
                                  onClick={(e) => handleDeleteTransaction(txn.id, e)}
                                  title="Delete this transaction"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // Snapshots View (legacy)
            !hasSnapshots ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>No snapshots found</p>
                <p style={styles.emptySubtext}>
                  Save your current experiment to create a snapshot
                </p>
              </div>
            ) : (
              <div style={styles.historyList}>
                {history.map((entry) => (
                  <div
                    key={entry.id}
                    style={{
                      ...styles.historyItem,
                      ...(selectedTransactionId === entry.id ? styles.historyItemSelected : {}),
                    }}
                    onClick={() => setSelectedTransactionId(entry.id)}
                  >
                    <div style={styles.historyItemContent}>
                      <div style={styles.historyItemHeader}>
                        <h3 style={styles.historyItemName}>{entry.name}</h3>
                        <div style={styles.historyItemMeta}>
                          <span style={styles.metaItem}>
                            <Calendar size={14} />
                            {formatDate(entry.timestamp)}
                          </span>
                          <span style={styles.metaItem}>
                            <Clock size={14} />
                            {formatTime(entry.timestamp)}
                          </span>
                        </div>
                      </div>
                      <div style={styles.historyItemDetails}>
                        <span style={styles.detailBadge}>
                          {entry.experiment.experimentType}
                        </span>
                        <span style={styles.detailBadge}>
                          {entry.experiment.reactionType}
                        </span>
                        {entry.experiment.materials.length > 0 && (
                          <span style={styles.detailBadge}>
                            {entry.experiment.materials.length} materials
                          </span>
                        )}
                        {entry.experiment.steps && entry.experiment.steps.length > 0 && (
                          <span style={styles.detailBadge}>
                            {entry.experiment.steps.length} steps
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={styles.historyItemActions}>
                      <button
                        style={styles.loadButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadSnapshot(entry.id);
                        }}
                        title="Load this experiment"
                      >
                        Load
                      </button>
                      <button
                        style={styles.deleteButton}
                        onClick={(e) => handleDeleteSnapshot(entry.id, e)}
                        title="Delete this entry"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    width: '90%',
    maxWidth: '900px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#1e293b',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  viewModeToggle: {
    display: 'flex',
    gap: '4px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '2px',
    backgroundColor: '#fff',
  },
  viewModeButton: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    color: '#64748b',
    border: 'none',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  viewModeButtonActive: {
    backgroundColor: '#2563eb',
    color: '#fff',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  clearButton: {
    padding: '8px 12px',
    backgroundColor: 'transparent',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#64748b',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flexGrow: 1,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyText: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: 500,
    color: '#64748b',
  },
  emptySubtext: {
    margin: 0,
    fontSize: '14px',
    color: '#94a3b8',
  },
  transactionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  experimentGroup: {
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  experimentHeader: {
    padding: '16px',
    backgroundColor: '#f8fafc',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  experimentHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  chevron: {
    color: '#64748b',
    flexShrink: 0,
  },
  experimentName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
  },
  experimentMeta: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginTop: '4px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: '#64748b',
  },
  transactionsGroup: {
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  transactionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  transactionItemSelected: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  transactionContent: {
    flex: 1,
  },
  transactionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  transactionNumber: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#2563eb',
    minWidth: '40px',
  },
  transactionTypeBadge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
  },
  transactionTime: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#64748b',
    marginLeft: 'auto',
  },
  transactionDescription: {
    fontSize: '13px',
    color: '#1e293b',
    marginBottom: '6px',
  },
  transactionDetails: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginTop: '6px',
  },
  detailTag: {
    padding: '2px 6px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    borderRadius: '3px',
    fontSize: '10px',
  },
  transactionActions: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    marginLeft: '12px',
  },
  viewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '4px 6px',
    backgroundColor: 'transparent',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  historyItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  historyItemSelected: {
    borderColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  historyItemContent: {
    flex: 1,
  },
  historyItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  historyItemName: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
  },
  historyItemMeta: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  historyItemDetails: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  detailBadge: {
    padding: '4px 8px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
  },
  historyItemActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  loadButton: {
    padding: '6px 12px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
};
